import { Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core'
import type { Producto, Proveedor } from '../../domain/entidades'
import { etiquetaProveedor } from '../../domain/entidades'
import { environment } from '../../../environments/environment'

const URL_SERVICIO_EXPORT = environment.exportServiceUrl || 'http://localhost:8000'

type Estado = 'seleccion' | 'generando' | 'listo' | 'error'

/** Un proveedor con sus productos ya filtrados por el buscador, para la vista agrupada del export. */
interface GrupoProveedor {
  codigo: string
  etiqueta: string
  productos: Producto[]
}

/** Exportamos productos seleccionados a PowerPoint mediante el servicio configurado. */
@Component({
  selector: 'app-export-modal',
  standalone: true,
  templateUrl: './export-modal.html',
  styleUrl: './export-modal.scss',
})
export class ExportModalComponent implements OnInit {
  @Input({ required: true }) productos: Producto[] = []
  @Input() proveedores: Proveedor[] = []
  @Output() cerrar = new EventEmitter<void>()

  readonly seleccionados = signal<Set<string>>(new Set())
  readonly expandidos = signal<Set<string>>(new Set())
  readonly estado = signal<Estado>('seleccion')
  readonly mensajeError = signal('')
  readonly filtro = signal('')

  ngOnInit(): void {
    this.seleccionados.set(new Set(this.productos.map((p) => p.id)))
  }

  get productosFiltrados(): Producto[] {
    const texto = this.filtro().trim().toLowerCase()
    if (!texto) return this.productos
    return this.productos.filter((p) =>
      [p.proveedorCodigo, p.mueble, p.nombreComercial, p.id].some((campo) =>
        (campo || '').toLowerCase().includes(texto),
      ),
    )
  }

  /** Agrupa los productos filtrados por proveedor — el orden respeta la primera aparición de cada proveedor. */
  get gruposFiltrados(): GrupoProveedor[] {
    const grupos = new Map<string, GrupoProveedor>()
    for (const p of this.productosFiltrados) {
      let grupo = grupos.get(p.proveedorCodigo)
      if (!grupo) {
        const nombre = this.proveedores.find((prov) => prov.codigo === p.proveedorCodigo)?.nombre
        grupo = { codigo: p.proveedorCodigo, etiqueta: etiquetaProveedor(p.proveedorCodigo, nombre), productos: [] }
        grupos.set(p.proveedorCodigo, grupo)
      }
      grupo.productos.push(p)
    }
    return [...grupos.values()]
  }

  alFiltrar(valor: string): void {
    this.filtro.set(valor)
  }

  alternarExpandido(codigo: string): void {
    this.expandidos.update((prev) => {
      const copia = new Set(prev)
      if (copia.has(codigo)) copia.delete(codigo)
      else copia.add(codigo)
      return copia
    })
  }

  todosProductosDelGrupoSeleccionados(grupo: GrupoProveedor): boolean {
    return grupo.productos.every((p) => this.seleccionados().has(p.id))
  }

  algunosProductosDelGrupoSeleccionados(grupo: GrupoProveedor): boolean {
    return !this.todosProductosDelGrupoSeleccionados(grupo) && grupo.productos.some((p) => this.seleccionados().has(p.id))
  }

  contarSeleccionadosDelGrupo(grupo: GrupoProveedor): number {
    return grupo.productos.filter((p) => this.seleccionados().has(p.id)).length
  }

  /** El chulo del proveedor selecciona/deselecciona TODOS sus productos, sin necesidad de expandirlo. */
  alternarGrupo(grupo: GrupoProveedor): void {
    const marcarTodos = !this.todosProductosDelGrupoSeleccionados(grupo)
    this.seleccionados.update((prev) => {
      const copia = new Set(prev)
      for (const p of grupo.productos) {
        if (marcarTodos) copia.add(p.id)
        else copia.delete(p.id)
      }
      return copia
    })
  }

  alternar(id: string): void {
    this.seleccionados.update((prev) => {
      const copia = new Set(prev)
      if (copia.has(id)) copia.delete(id)
      else copia.add(id)
      return copia
    })
  }

  seleccionarTodos(): void {
    this.seleccionados.set(new Set(this.productosFiltrados.map((p) => p.id)))
  }

  deseleccionarTodos(): void {
    this.seleccionados.update((prev) => {
      const copia = new Set(prev)
      for (const p of this.productosFiltrados) copia.delete(p.id)
      return copia
    })
  }

  /** Un solo checkbox reemplaza los dos botones: marca/desmarca todo lo visible con el filtro actual. */
  get todosFiltradosSeleccionados(): boolean {
    return this.productosFiltrados.length > 0 && this.productosFiltrados.every((p) => this.seleccionados().has(p.id))
  }

  /** Estado intermedio (algunos si, otros no) para que el checkbox muestre el guion en vez de vacio o marcado. */
  get algunosFiltradosSeleccionados(): boolean {
    return !this.todosFiltradosSeleccionados && this.productosFiltrados.some((p) => this.seleccionados().has(p.id))
  }

  alternarTodos(): void {
    if (this.todosFiltradosSeleccionados) this.deseleccionarTodos()
    else this.seleccionarTodos()
  }

  alClicOverlay(e: MouseEvent): void {
    if (e.target === e.currentTarget) this.cerrar.emit()
  }

  async generar(): Promise<void> {
    this.estado.set('generando')
    try {
      const elegidos = this.productos.filter((p) => this.seleccionados().has(p.id))
      const resp = await fetch(`${URL_SERVICIO_EXPORT}/exportar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productos: elegidos }),
      })
      if (!resp.ok) throw new Error(`Servicio de export respondió ${resp.status}`)

      // El backend ya no devuelve el .pptx directo: una invocación síncrona
      // de Lambda vía API Gateway tiene un límite duro de 6MB de respuesta,
      // y con pocos productos reales con fotos el archivo ya lo superaba
      // (confirmado en vivo el 17 sep 2026 con solo 3 productos). Ahora sube
      // el archivo a S3 y devuelve una URL firmada de descarga — acá solo
      // hace falta abrirla para que el navegador descargue directo desde ahí.
      const { url }: { url: string } = await resp.json()
      const a = document.createElement('a')
      a.href = url
      a.download = 'Feria_China_2026.pptx'
      a.click()
      this.estado.set('listo')
    } catch (err) {
      this.mensajeError.set(err instanceof Error ? err.message : 'No se pudo generar la presentación.')
      this.estado.set('error')
    }
  }

  reintentar(): void {
    this.estado.set('seleccion')
  }
}
